import React, { useEffect, useMemo, useState } from 'react'
import Navbar from '../Navbar'
import TransactionInfo from './TransactionInfo'
import axios from 'axios'
import { useSelector } from 'react-redux'
import {
  FiCreditCard,
  FiCheckCircle,
  FiTrendingDown
} from 'react-icons/fi'

export default function Payments() {

  const { url, transactions } = useSelector(state => state)

  const [application, setApplication] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')


  useEffect(() => {

    const getLoanApplication = async () => {

      try {

        const response = await axios.get(
          `${url}/auth/my-loan-application`,
          {
            headers: {
              Authorization:
                `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        )

        setApplication(response.data)

      } catch (err) {

      console.error(
        'Unable to load loan information:',
        err
      )

      if (err.response?.status === 404) {

        setApplication({
          has_application: false,
          status: null,
          loan: null
        })

        setError('')
        return
      }

      setError(
        err.response?.data?.detail ||
        'Unable to load your transaction information.'
      )

    } finally {

        setLoading(false)
      }
    }

    getLoanApplication()

  }, [url])


  const sortedTransactions = useMemo(() => {

    return [...(transactions || [])].sort(
      (a, b) =>
        new Date(b.paid_date || b.date) -
        new Date(a.paid_date || a.date)
    )

  }, [transactions])


  const totalPaid = useMemo(() => {

    return sortedTransactions.reduce(
      (total, transaction) =>
        total + Number(transaction.amount || 0),
      0
    )

  }, [sortedTransactions])


  const activeLoan =
    application?.status === 'active'
      ? application.loan
      : null


  const outstandingBalance =
    Number(
      activeLoan?.outstanding_balance || 0
    )


  if (loading) {

    return (

      <div className="
        min-h-screen
        bg-[#F7F9FC]
        font-[Satoshi]
      ">

        <Navbar />

        <main className="
          lg:ml-[15em]
          min-w-0
          min-h-screen
          flex-1
          bg-[#F7F9FC]
          px-5
          pb-10
          pt-24
          sm:px-6
          lg:px-8
          lg:pt-8
        ">

          <div className="
            mx-auto
            max-w-[1500px]
          ">

            <div className="
              animate-pulse
              space-y-5
            ">

              <div className="
                h-8
                bg-gray-200
                rounded-lg
                w-56
              " />

              <div className="
                h-4
                bg-gray-100
                rounded
                w-80
              " />

              <div className="
                grid
                grid-cols-1
                md:grid-cols-3
                gap-5
                mt-8
              ">

                {[1, 2, 3].map(item => (

                  <div
                    key={item}
                    className="
                      h-32
                      bg-gray-100
                      rounded-2xl
                    "
                  />

                ))}

              </div>

            </div>

          </div>

        </main>

      </div>
    )
  }


  return (

    <div className="
      min-h-screen
      bg-[#F7F9FC]
      font-[Satoshi]
      text-gray-900
    ">

      <Navbar />


      <main className="
        lg:ml-[15em]
        min-w-0
        min-h-screen
        flex-1
        bg-[#F7F9FC]
        px-5
        pb-10
        pt-24
        sm:px-6
        lg:px-8
        lg:pt-8
      ">

        <div className="
          mx-auto
          max-w-[1500px]
        ">


          {/* HEADER */}

          <div className="
            mb-6
            sm:mb-8
          ">

            <p className="
              text-sm
              font-semibold
              text-green-600
            ">
              TRANSACTIONS
            </p>

            <h1 className="
              mt-1
              text-2xl
              sm:text-3xl
              font-bold
              tracking-tight
              text-gray-900
            ">
              Transaction history
            </h1>

            <p className="
              mt-2
              text-sm
              leading-6
              text-gray-500
            ">
              Track all repayments made toward your RentHub loan.
            </p>

          </div>


          {/* ERROR */}

          {error && (

            <div className="
              bg-red-50
              border
              border-red-200
              text-red-600
              rounded-xl
              px-5
              py-4
              mb-6
              text-sm
            ">
              {error}
            </div>

          )}


          {/* SUMMARY CARDS */}

          <div className="
            grid
            grid-cols-1
            md:grid-cols-3
            gap-5
            mb-8
          ">

            <SummaryCard
              icon={<FiCreditCard />}
              title="Total repaid"
              value={formatCurrency(totalPaid)}
            />

            <SummaryCard
              icon={<FiCheckCircle />}
              title="Payments made"
              value={sortedTransactions.length}
            />

            <SummaryCard
              icon={<FiTrendingDown />}
              title="Outstanding balance"
              value={
                activeLoan
                  ? formatCurrency(
                      outstandingBalance
                    )
                  : '—'
              }
            />

          </div>


          {/* TRANSACTIONS */}

          <section className="
            bg-white
            border
            border-gray-200
            rounded-2xl
            shadow-sm
            overflow-hidden
          ">

            <div className="
              flex
              items-center
              justify-between
              px-6
              py-5
              border-b
              border-gray-100
            ">

              <div>

                <h2 className="
                  text-lg
                  font-bold
                ">
                  Repayment activity
                </h2>

                <p className="
                  text-sm
                  text-gray-500
                  mt-1
                ">
                  {sortedTransactions.length === 0
                    ? 'No payments recorded yet.'
                    : `${sortedTransactions.length} ${
                        sortedTransactions.length === 1
                          ? 'transaction'
                          : 'transactions'
                      }`
                  }
                </p>

              </div>

            </div>


            {sortedTransactions.length > 0 ? (

              <>

                {/* DESKTOP HEADER */}

                <div className="
                  hidden
                  md:grid
                  md:grid-cols-[1.5fr_1fr_1fr_120px]
                  gap-6
                  px-6
                  py-3
                  bg-gray-50
                  border-b
                  border-gray-100
                  text-xs
                  font-bold
                  uppercase
                  tracking-wide
                  text-gray-400
                ">

                  <span>
                    Reference
                  </span>

                  <span>
                    Date
                  </span>

                  <span>
                    Amount
                  </span>

                  <span>
                    Status
                  </span>

                </div>


                <div>

                  {sortedTransactions.map(
                    (transaction, index) => (

                      <TransactionInfo
                        key={
                          transaction.reference ||
                          index
                        }
                        trans_id={
                          transaction.reference
                        }
                        amount={
                          transaction.amount
                        }
                        paid_date={
                          transaction.paid_date ||
                          transaction.date
                        }
                      />

                    )
                  )}

                </div>

              </>

            ) : (

              <div className="
                flex
                flex-col
                items-center
                justify-center
                text-center
                px-6
                py-16
              ">

                <div className="
                  w-12
                  h-12
                  rounded-xl
                  bg-green-50
                  text-green-600
                  flex
                  items-center
                  justify-center
                  text-xl
                  mb-4
                ">

                  <FiCreditCard />

                </div>

                <h3 className="
                  font-bold
                  text-gray-900
                ">
                  No transactions yet
                </h3>

                <p className="
                  text-sm
                  text-gray-500
                  mt-2
                  max-w-sm
                ">
                  Your successful RentHub repayments will appear here.
                </p>

              </div>

            )}

          </section>

        </div>

      </main>

    </div>
  )
}



function SummaryCard({
  icon,
  title,
  value
}) {

  return (

    <div className="
      bg-white
      border
      border-gray-200
      rounded-2xl
      p-5
      shadow-sm
    ">

      <div className="
        flex
        items-center
        gap-4
      ">

        <div className="
          w-11
          h-11
          shrink-0
          rounded-xl
          bg-green-50
          text-green-600
          flex
          items-center
          justify-center
          text-xl
        ">
          {icon}
        </div>


        <div>

          <p className="
            text-xs
            uppercase
            tracking-wide
            font-semibold
            text-gray-400
          ">
            {title}
          </p>

          <p className="
            text-xl
            lg:text-2xl
            font-bold
            mt-1
          ">
            {value}
          </p>

        </div>

      </div>

    </div>
  )
}



function formatCurrency(amount) {

  return new Intl.NumberFormat(
    'en-NG',
    {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }
  ).format(
    Number(amount || 0)
  )
}